import { createApp } from 'vue';
import { assign, capitalize, upperFirst, range } from 'lodash';
import App from './App';
import { ConfigurationService } from '@nsftx/games-config';
import * as packageTranslations from '@nsftx/i18n-lucky-x';
import gamesStatic from '@nsftx/games-sdk-js/src/api/gamesStatic';
import createTranslationsProxy from '@nsftx/games-sdk-js/src/utility/translationsProxy';
import sentry from '@nsftx/games-sdk-js/src/utility/sentry';
import { busService, webViewIntegration } from './utility';
import LocalizedLabel from '@/components/LocalizedLabel';
import router from './router';
import store from './store';

const qp = new URLSearchParams(window.location.search);
const tenantId = qp.get('tenantId');
const deliveryPlatform = qp.get('deliveryPlatform') || qp.get('application') || 'web';
const messageProtocol = qp.get('messageProtocol') || qp.get('integrationType') || 'postMessage';
/**
 * Used only to display correct layout and style, without considering any logic.
 * You should use only this prop for layout and style differences between web and terminal.
 *
 * Possible values:
 * - `"terminal"`: Renders the terminal-style layout.
 * - `"web"`: Renders the standard web layout.
 *
 * Default value: `"web"`
 * @type {"terminal" | "web"}
 */
const layout = qp.get('layout') || 'web';
const token = qp.get('token');
const clientType = qp.get('authStrategy') || qp.get('clientType');
const playerId = qp.get('id') || qp.get('playerId');
let productUuid = null;
const deployment = window.location.hostname.includes('nsoft.app') ? 'cf' : 'netlify';
const platform = qp.get('platform');
const customDomain = qp.get('customDomain');
const isNextGen = platform === 'NextGen';
const parent = qp.get('parent');
const nextGenStandaloneIntegration = typeof parent !== 'undefined' && parent === 'false';
let productName = qp.get('productName');
const productNamePrefixes = ['SRMV', 'NVNV', 'NSNV'];
for (const productPrefix of productNamePrefixes) {
  if (productName.startsWith(productPrefix)) productName = productName.slice(productPrefix.length);
}
window.addEventListener('message', (event) => {
  if (event.data.action === 'Slave.Load') {
    productUuid = event.data.data.account.uuid;
  }

  if (deliveryPlatform.toLowerCase() === 'terminal') {
    store.dispatch('handleTerminalMessages', event);
  }
});
if (isNextGen) {
  window.addEventListener('message', (event) => {
    if (event.data.action === 'User.AuthorizationChanged') {
      const authData = event.data.data.auth;
      const loggedIn = !!authData.token;
      authData.user.logged = loggedIn;
      store.dispatch('setUserBalance', authData.user.balance);
      store.dispatch('user/updateUser', {
        auth: authData,
        profile: authData.user,
      });
    }
    if (event.data.action === 'User.BalanceChanged') {
      store.dispatch('setUserBalance', event.data.data.balance);
    }
  });

  window.addEventListener('Configuration:SetConfiguration', (event) => {
    store.dispatch('setNextGenConfig', event.detail);
  });
}

const checkLocale = (locale) => {
  switch (locale) {
    case 'sr':
      return 'sr-Latn';
    default:
      return locale;
  }
};

const getSlaveId = (id) => {
  if (id === '/ticket-history') {
    return `PluginLuckyXTicketHistory`;
  }
  if (id === '/results') {
    return `PluginLuckyXResults`;
  }
  return `PluginLuckyX`;
};
const clientAdapters = [];
const nextGenAdapterData = {
  name: 'IgniteAdapter',
  options: {
    allowedOrigins: '*',
    displayId: 'luckyx',
    version: '1.1',
    providerId: process.env.VUE_APP_ENVIRONMENT === 'production' ? 30 : 26,
    environment: process.env.VUE_APP_ENVIRONMENT,
  },
};
const gravityGatewayAdapterData = {
  name: `${upperFirst(messageProtocol)}Adapter`,
  options: {
    slaveId: getSlaveId(window.location.pathname),
    eventPropagation: {
      click: true,
      keydown: '*',
    },
  },
};
if (isNextGen) {
  clientAdapters.push(nextGenAdapterData);
} else {
  clientAdapters.push(gravityGatewayAdapterData);
}

(async () => {
  const configSettings = {
    environment: process.env.NODE_ENV,
    requiredPaths: [],
    applicationName: capitalize(deliveryPlatform),
    productName: productName || 'LuckyX',
    channel: deliveryPlatform,
    messageProtocol,
    clientAdapters,
    ui: tenantId,
    deployment,
    customDomain,
  };
  const usePackageTranslations = store.state.usePackageTranslations;
  if (!usePackageTranslations) configSettings.translationDomains = ['web'];
  const configService = new ConfigurationService(configSettings);
  const config = await configService.getConfiguration();
  // eslint-disable-next-line no-console
  console.log('CONFIG FROM CONFIG SERVICE:', config);
  config.locale = checkLocale(config.locale);
  config.customDomain = customDomain;

  const betslipStaticConfig = config.ui?.config?.betslip;
  const paymentChips = config.ui?.config?.paymentChips;
  const betslipConfig = {
    betLayout: betslipStaticConfig?.betLayout ?? 'A',
    channel: deliveryPlatform,
    ticketTypes: betslipStaticConfig?.ticketTypes ?? ['single'],
    rules: config.rules,
    taxes: config.taxes,
    isPossibleWinActive: betslipStaticConfig?.isPossibleWinActive ?? false,
    isPaymentBetAllowed: betslipStaticConfig?.isPaymentBetAllowed ?? true,
    isPaymentPerBetAllowed: betslipStaticConfig?.isPaymentPerBetAllowed ?? true,
    isFuturePerBetAllowed: betslipStaticConfig?.isFuturePerBetAllowed ?? false,
    isFuturePerTicketAllowed: config.futureBets.value !== 1,
    isTicketCancelAllowed: config.cancelEnabled ?? true,
    isTicketRebetAllowed: betslipStaticConfig?.isTicketRebetAllowed ?? true,
    isAddButtonActive: true,
    minIncrement: betslipStaticConfig?.minIncrement ?? 0.01,
    futureRounds: range(1, config.futureBets.value + 1),
    paymentChips,
  };
  await store.dispatch('gamesBetslip/setConfig', betslipConfig);
  await store.dispatch('setDeviceLayout');
  await store.dispatch('setConfig', config);
  if (isNextGen) {
    await store.dispatch('updateConfigWithNextGenConfig');
    store.dispatch('setNextGenStandaloneIntegration', nextGenStandaloneIntegration);
  }
  await store.dispatch('setBets', config.bets);
  await store.dispatch('setBetLimits', config.betLimit);
  await store.dispatch('setRules', config.rules);
  await store.dispatch('setLayout', layout);
  if (layout === 'terminal') {
    await store.dispatch('setActiveComponent', 'BettingArea');
  }
  store.state.config.productUuid = productUuid;
  if (deliveryPlatform === 'terminal') {
    store.state.config.device = store.getters.device;
    store.state.config.betshop = store.getters.betshop;
    store.state.config.companyData = store.getters.companyData;
    store.state.config.account = store.getters.account;
  }
  if (usePackageTranslations) {
    // NPM PACKED TRANS
    const translations = await packageTranslations.exportTranslations(config.locale, {
      companyName: config.tenantName || config.rawData?.companyInfo?.name,
      companyUuid: tenantId,
      env: process.env.NODE_ENV,
    });
    const proxyTranslations = createTranslationsProxy(translations, ['luckyx_']);
    await store.dispatch('setTranslations', proxyTranslations);
  } else {
    // STANDARD STATIC + HTTP CONFIG TRANS
    const translations = await gamesStatic.getTranslations(config.locale);
    await store.dispatch('setTranslations', assign(config.translations, translations));
  }
  if (token && config.ui.config.mode === 'standalone') {
    store.state.config.platformConfig.clientType = clientType;
    store.state.config.platformConfig.playerId = playerId;
    await store.dispatch('user/setUserSessionToken', token);
    await store.dispatch('getPlayer', { config, token });
  }
  busService.init();

  const app = createApp({
    extends: App,
    beforeCreate() {
      if (webViewIntegration.isActive()) {
        if (window.WebAppListener) {
          window.WebAppListener.sendMessage('Slave.Init');
        }
      }
    },
  });

  if (process.env.VUE_APP_SENTRY_ACTIVE === 'true') {
    sentry.start({
      app,
      dsn: process.env.VUE_APP_SENTRY_DSN,
      sampleRate: 0.01,
      trackComponents: true,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Network Error',
        'timeout of 5000ms exceeded',
        'timeout of 10000ms exceeded',
        'Android is not defined',
        /.*NotAllowedError.*/gm,
        /.*WakeLock.*/gm,
      ],
    });
  }

  app.config.productionTip = false;
  app.use(LocalizedLabel);
  app.use(router).use(store).mount('#app');
})();
